import React from 'react';
import { XMarkIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { Mission } from '../../types';

interface SendAlertModalProps {
  mission: Mission;
  onConfirm: () => void;
  onCancel: () => void;
  sending: boolean;
}

const SendAlertModal: React.FC<SendAlertModalProps> = ({
  mission,
  onConfirm,
  onCancel,
  sending
}) => {
  // Format the alert message preview (matches backend formatting)
  const formatAlertPreview = () => {
    const lines: string[] = [
      '⚠️ TEST ALERT - NOT A REAL EMERGENCY',
      '🚁 TEAAM MISSION ALERT',
      '━━━━━━━━━━━━━━━━━━━━━━',
      '',
    ];

    // Primary Base - highlighted at the top
    if (mission.primaryBase) {
      lines.push(`🏥 BASE: ${mission.primaryBase.name.toUpperCase()}`);
      lines.push('');
    }

    lines.push(
      `Mission: ${mission.missionNumber}`,
      `Type: ${formatMissionType(mission.missionType)}`,
      `Priority: ${mission.priority}`,
      `Status: ${mission.status}`,
      ''
    );

    // Location information
    if (mission.sceneLocation) {
      lines.push(`📍 Location: ${mission.sceneLocation}`);
    }
    if (mission.destinationLocation) {
      lines.push(`🏥 Destination: ${mission.destinationLocation}`);
    }
    if (mission.sceneLocation || mission.destinationLocation) {
      lines.push('');
    }

    // Patient information
    const patientCount = mission.patientCount || 1;
    lines.push(`👤 Patient: ${patientCount} patient(s)`);

    const firstPatient = mission.patients?.[0];
    if (firstPatient?.chiefComplaint) {
      lines.push(`   ${firstPatient.chiefComplaint}`);
    }
    lines.push('');

    // Resources
    const firstAircraft = mission.aircraft?.[0];
    if (firstAircraft) {
      const contractor = firstAircraft.contractor?.companyName || firstAircraft.aircraftType;
      const tail = firstAircraft.tailNumber;
      const aircraft = tail ? `${contractor} (${tail})` : contractor;
      if (aircraft) {
        lines.push(`✈️ Aircraft: ${aircraft}`);
      }
    }

    // Caller information
    if (mission.callerAgency || mission.callerName) {
      const caller = [mission.callerAgency, mission.callerName]
        .filter(Boolean)
        .join(' - ');
      lines.push(`📞 Caller: ${caller}`);
    }

    // Timing
    if (mission.timeReceived) {
      const time = new Date(mission.timeReceived).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
      lines.push(`⏰ Received: ${time}`);
    }

    return lines.join('\n');
  };

  const formatMissionType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'SCENE_CALL': 'Scene Call',
      'INTER_FACILITY': 'Inter-Facility',
      'STANDBY': 'Standby',
      'TRAINING': 'Training',
      'REPATRIATION': 'Repatriation',
      'TELEHEALTH': 'Telehealth',
    };
    return typeMap[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={!sending ? onCancel : undefined}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-orange-500 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BellAlertIcon className="h-6 w-6 text-white" />
              <h3 className="text-lg font-semibold text-white">
                Confirm Send Alert
              </h3>
            </div>
            {!sending && (
              <button
                onClick={onCancel}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                This will send the following alert to the <strong>Connect Rocket "alerts" channel</strong>:
              </p>
            </div>

            {/* Alert Preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="text-sm font-mono whitespace-pre-wrap text-gray-800">
                {formatAlertPreview()}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Note:</strong> All alerts are currently prefixed with "TEST ALERT" for development/testing purposes.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={sending}
              className="btn btn-secondary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!sending) {
                  onConfirm();
                }
              }}
              disabled={sending}
              className="btn bg-orange-500 hover:bg-orange-600 text-white border-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BellAlertIcon className="h-4 w-4 mr-2" />
              {sending ? 'Sending Alert...' : 'Send Alert'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendAlertModal;
